/**
 * Dates a teacher is away (D-681).
 *
 * <b>The hard record — by design, and half-built.</b> Regular hours are soft:
 * outside them a parent is refused and a colleague is only warned, because a
 * teacher who has privately arranged cover must stay askable. Time off is meant
 * to refuse both, and the reason the two can differ is the escape hatch —
 * deleting a block that is no longer true is simply correct, where deleting a
 * weekly pattern to wave one day through throws away a rule that still holds.
 *
 * <b>Both halves exist now</b> (D-682). The private-request door refuses a
 * family; the cover ask, the colleague picker, the open board and the studio's
 * approval of a stand-in all refuse a colleague. The one screen that still
 * offers her name is the studio's own uncovered queue, which cannot narrow
 * because it lists rows on many dates against one colleague list — and the ask
 * refuses there too, with a sentence saying why.
 *
 * <b>Hers, with no approval.</b> It is live the moment she saves it, so nothing
 * here has a status and nothing waits on anybody.
 */

/**
 * One block she is away.
 *
 * <b>`to` is inclusive</b> — she says the 27th and means the 27th. That is
 * deliberately not the half-open rule the rest of the app uses for clock time:
 * whole days are not back-to-back the way lessons are, and an exclusive end
 * would make every screen print a date she did not say.
 */
export type TimeOffBlock = {
  id: string
  from: string
  to: string
  note: string | null

  /**
   * Classes inside the block that still need something doing about them
   * (D-683).
   *
   * <b>Counted, never arranged.</b> Booking a holiday raises no cover request,
   * cancels nothing and hands nothing over — a teacher who books a fortnight in
   * July has not decided who takes her Tuesdays, and the studio hearing about
   * it as eight questions is the fatigue the whole ask design exists to avoid.
   *
   * <b>Recounted on every read</b>, so it falls as she deals with them and a
   * session a colleague already took never appears in it.
   */
  unarranged: number

  /**
   * The earliest of those dates, or null.
   *
   * The link opens on this rather than on the first day of the block: a Monday
   * she has already sorted out would open an empty dialog, which reads as a
   * broken button (D-365).
   */
  firstUnarranged: string | null

  /**
   * The private lessons she gives inside the block, one per booking (D-730).
   *
   * <b>Never a class to declare against.</b> A private is hers to move or
   * cancel through its booking, so the block reminds her and points at the
   * day; it stays listed until she does.
   */
  privates: TimeOffPrivate[]
}

/** One private lesson inside a block of time off (D-730). */
export type TimeOffPrivate = {
  bookingId: string
  date: string
  startTime: string
  dancerNames: string | null
}

export async function fetchMyTimeOff(): Promise<TimeOffBlock[]> {
  const res = await fetch('/api/teacher/time-off')

  // A parent has no staff row and so no time off; the panel does not render.
  if (res.status === 404) return []
  if (!res.ok) throw new Error(`Could not load your time off (${res.status})`)

  return res.json()
}

export async function addTimeOff(
  from: string,
  to: string,
  note: string | null,
): Promise<TimeOffBlock> {
  const res = await fetch('/api/teacher/time-off', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ from, to, note }),
  })

  if (!res.ok) {
    // The server explains the refusal - an inverted pair, a block already
    // finished, a mistyped year. Passing it through beats a status code.
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.title ?? `Could not save (${res.status})`)
  }

  return res.json()
}

export async function removeTimeOff(id: string): Promise<void> {
  const res = await fetch(`/api/teacher/time-off/${id}`, { method: 'DELETE' })

  if (!res.ok) throw new Error(`Could not remove that (${res.status})`)
}

/**
 * How long a block is, in whole days, counting both ends.
 *
 * <b>Both ends, because the range is closed.</b> A single day is one, not
 * zero — which is the commonest block there is and the one an exclusive-end
 * calculation gets wrong in the direction nobody notices.
 */
export function daysInclusive(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00Z`)
  const end = Date.parse(`${to}T00:00:00Z`)

  if (Number.isNaN(start) || Number.isNaN(end)) return 0

  return Math.floor((end - start) / 86_400_000) + 1
}

/**
 * A block as one line: <i>20 Oct &ndash; 27 Oct (8 days)</i>, or one date when
 * it is a single day.
 *
 * <b>UTC-anchored</b>, so it is DST-proof: these are dates rather than
 * instants, and day arithmetic on a date is pure counting (D-68, D-485).
 */
export function blockLabel(from: string, to: string): string {
  const day = (iso: string) =>
    new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    })

  if (from === to) return day(from)

  // `days`, not `nights` - the range is closed, so a single day is one and
  // the two words differ by exactly the off-by-one this file exists to avoid.
  const days = daysInclusive(from, to)

  return `${day(from)} – ${day(to)} (${days} days)`
}

/**
 * Whether a block is over.
 *
 * <b>Takes today rather than reading the clock</b>, so a caller can pass the
 * app's own `today()` — the UTC date is tomorrow after about 7pm Central, which
 * is inside studio hours (D-68, D-485).
 */
export function isPast(block: TimeOffBlock, today: string): boolean {
  return block.to < today
}
